/**
 * Rule suggestions: rules the AI proposes after seeing the same kind of
 * problem in contracts the rule book doesn't cover. Nothing here affects an
 * analysis until finance accepts it.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, FileText, Lightbulb, RotateCcw, X } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  useAcceptSuggestion,
  useDismissSuggestion,
  useRestoreSuggestion,
  useRuleSuggestions,
  useRules
} from '../../../hooks/queries/useRules';
import { FinanceRuleInput, RuleSuggestion, RuleSuggestionStatus } from '../../../services/api/ruleService';
import { ClassificationBadge } from '../contract-workflow/shared/PlaybookReviewPanel';
import { RuleFormModal } from './RuleFormModal';
import { contractTypeLabel, personName } from './ruleBookConstants';

const STATUS_TABS: { id: RuleSuggestionStatus; label: string }[] = [
  { id: 'pending', label: 'To review' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'dismissed', label: 'Dismissed' }
];

const SuggestionCard: React.FC<{
  suggestion: RuleSuggestion;
  canEdit: boolean;
  busy: boolean;
  onAccept: () => void;
  onDismiss: (reason?: string) => void;
  onRestore: () => void;
}> = ({ suggestion: s, canEdit, busy, onAccept, onDismiss, onRestore }) => {
  const [showEvidence, setShowEvidence] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <li className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">{s.title}</h3>
            <ClassificationBadge classification={s.severity} />
            <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-dark-bg-secondary text-xs text-gray-600 dark:text-gray-300">
              {s.category}
            </span>
          </div>
          <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-300">{s.ruleText}</p>
          {s.rationale && (
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium">Why:</span> {s.rationale}
            </p>
          )}
          {s.suggestedPosition && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium">Our position:</span> {s.suggestedPosition}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            {s.appliesTo?.length ? s.appliesTo.map(contractTypeLabel).join(', ') : 'All contract types'}
            {' '}&middot; last seen {new Date(s.lastSeenAt).toLocaleDateString()}
            {s.reviewedAt && <> &middot; {s.status} by {personName(s.reviewedBy)} {new Date(s.reviewedAt).toLocaleDateString()}</>}
          </p>
          {s.status === 'dismissed' && s.dismissReason && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Reason: {s.dismissReason}</p>
          )}
        </div>

        <div className="flex sm:flex-col items-center sm:items-end gap-2 flex-shrink-0">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
              s.contractCount > 1
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Seen in {s.contractCount} contract{s.contractCount === 1 ? '' : 's'}
          </span>

          {canEdit && s.status === 'pending' && !dismissing && (
            <div className="flex gap-2">
              <button
                onClick={() => setDismissing(true)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-secondary"
              >
                <X className="w-4 h-4" /> Dismiss
              </button>
              <button
                onClick={onAccept}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:hover:bg-accent-400 dark:text-gray-900"
              >
                <Check className="w-4 h-4" /> Review &amp; add
              </button>
            </div>
          )}
          {canEdit && s.status === 'dismissed' && (
            <button
              onClick={onRestore}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-secondary"
            >
              <RotateCcw className="w-4 h-4" /> Restore
            </button>
          )}
        </div>
      </div>

      {dismissing && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            autoFocus
            value={reason}
            maxLength={500}
            onChange={e => setReason(e.target.value)}
            placeholder="Why not? (optional, helps the team later)"
            aria-label="Reason for dismissing"
            className="flex-1 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button onClick={() => { setDismissing(false); setReason(''); }} className="px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
              Cancel
            </button>
            <button
              onClick={() => onDismiss(reason.trim() || undefined)}
              disabled={busy}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
            >
              Dismiss suggestion
            </button>
          </div>
        </div>
      )}

      {s.evidence.length > 0 && (
        <div className="mt-3 border-t border-gray-100 dark:border-dark-border pt-3">
          <button
            onClick={() => setShowEvidence(!showEvidence)}
            aria-expanded={showEvidence}
            className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-accent-400 hover:underline"
          >
            {showEvidence ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {showEvidence ? 'Hide' : 'Show'} where it was found
          </button>
          {showEvidence && (
            <ul className="mt-3 space-y-3">
              {s.evidence.map((e, i) => (
                <li key={`${e.contractKey}-${i}`} className="text-sm">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {e.contractId ? (
                      <Link to={`/contracts/${e.contractId}`} className="font-medium text-gray-700 dark:text-gray-200 hover:underline">
                        {e.fileName || 'Contract'}
                      </Link>
                    ) : (
                      <span className="font-medium text-gray-700 dark:text-gray-200">{e.fileName || 'Contract'}</span>
                    )}
                    {e.clauseRef && <> &middot; clause {e.clauseRef}</>}
                    {' '}&middot; {new Date(e.observedAt).toLocaleDateString()}
                  </div>
                  <blockquote className="mt-1 border-l-2 border-gray-300 dark:border-dark-border pl-3 italic text-gray-700 dark:text-gray-300">
                    {e.quote}
                  </blockquote>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

export const SuggestionsTab: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('rules.edit');

  const [status, setStatus] = useState<RuleSuggestionStatus>('pending');
  const { data, isLoading, error } = useRuleSuggestions(status);
  const { data: rulesData } = useRules();
  const accept = useAcceptSuggestion();
  const dismiss = useDismissSuggestion();
  const restore = useRestoreSuggestion();

  const [reviewing, setReviewing] = useState<RuleSuggestion | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const suggestions = data?.suggestions ?? [];
  const busy = accept.isPending || dismiss.isPending || restore.isPending;

  // Memoised so the form isn't reset on every render
  const initial = useMemo<FinanceRuleInput | undefined>(() => reviewing ? {
    title: reviewing.title,
    ruleText: reviewing.ruleText,
    severity: reviewing.severity,
    category: reviewing.category,
    appliesTo: reviewing.appliesTo || [],
    suggestedPosition: reviewing.suggestedPosition || ''
  } : undefined, [reviewing]);

  const run = (p: Promise<unknown>) => {
    setActionError(null);
    p.catch((e: Error) => setActionError(e.message));
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Suggestion status">
        {STATUS_TABS.map(t => (
          <button
            key={t.id}
            role="tab"
            aria-selected={status === t.id}
            onClick={() => setStatus(t.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              status === t.id
                ? 'bg-gray-900 border-gray-900 text-white dark:bg-white dark:border-white dark:text-gray-900'
                : 'border-gray-300 dark:border-dark-border text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary'
            }`}
          >
            {t.label}
            {t.id === 'pending' && (data?.pendingCount ?? 0) > 0 && ` (${data?.pendingCount})`}
          </button>
        ))}
      </div>

      {actionError && (
        <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </p>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1].map(i => <div key={i} className="h-32 rounded-xl bg-gray-100 dark:bg-dark-bg-secondary animate-pulse" />)}
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Couldn't load suggestions: {(error as Error).message}</p>
      ) : suggestions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-dark-border p-10 text-center">
          <Lightbulb className="w-10 h-10 mx-auto text-gray-400" />
          <h3 className="text-base mt-3 font-medium text-gray-900 dark:text-white">
            {status === 'pending' ? 'Nothing to review' : `No ${status} suggestions`}
          </h3>
          {status === 'pending' && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              After each contract analysis, the AI looks for serious problems your rule book doesn't cover yet and
              proposes rules here. They only take effect once you add them.
            </p>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {suggestions.map(s => (
            <SuggestionCard
              key={s._id}
              suggestion={s}
              canEdit={canEdit}
              busy={busy}
              onAccept={() => { accept.reset(); setReviewing(s); }}
              onDismiss={reason => run(dismiss.mutateAsync({ id: s._id, reason }))}
              onRestore={() => run(restore.mutateAsync(s._id))}
            />
          ))}
        </ul>
      )}

      {reviewing && (
        <RuleFormModal
          rule={null}
          initial={initial}
          heading="Add suggested rule"
          submitLabel="Add to rule book"
          categories={rulesData?.categories ?? []}
          saving={accept.isPending}
          error={accept.error?.message ?? null}
          onSave={input => accept.mutate({ id: reviewing._id, input }, { onSuccess: () => setReviewing(null) })}
          onClose={() => setReviewing(null)}
        />
      )}
    </div>
  );
};

export default SuggestionsTab;
